import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import {
    ContainerComponent,
    GtmService,
    ICompanyInfo,
    IconComponent,
    ICustomWindow,
    IFooter,
    HeadingComponent,
    ILink,
    ISocialMediaItem,
    LinkDirective,
    LinkService,
    SnowplowService,
    SocialMediaIconService,
    TrackingEvents,
    WindowRefService,
} from '@sirius/common';
import { PsbButtonDirective } from '../../directives/psb-button/psb-button.directive';

@Component({
    selector: 'butterfly-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'role': 'contentinfo',
        'class': 'sir-text-context-on-dark',
    },
    imports: [
        ContainerComponent,
        HeadingComponent,
        IconComponent,
        LinkDirective,
        PsbButtonDirective,
        TranslateModule,
    ]
})
export class FooterComponent implements OnInit {
    @Input({ required: true }) footer!: IFooter;
    @Input({ required: true }) companyInfo!: ICompanyInfo;
    @Input({ required: true }) socialMediaLinks!: ISocialMediaItem[];
    @Input() culture?: string;

    currentYear = new Date().getFullYear();
    isChina = false;

    private _socialMediaIconService = inject(SocialMediaIconService);
    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);
    private _linkService = inject(LinkService);
    private _windowRef = inject(WindowRefService);
    private _select = new Subject<{ link: ILink; anchor: HTMLElement }>();

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    ngOnInit(): void {
        this.isChina = this.culture === 'zh-Hans';

        this.socialMediaLinks.forEach(
            (x: ISocialMediaItem) =>
                (x.icon = this._socialMediaIconService.getIconName(x.itemType)),
        );

        this.addTracking();
    }

    onClick(item: { link: ILink; event: Event }) {
        const tar = item.event.target as HTMLElement;
        this._select.next({ link: item.link, anchor: tar });
    }

    //#region tracking
    addTracking(): void {
        if (!this.Window) {
            return;
        }

        this._select.subscribe((x: { link: ILink; anchor: HTMLElement }) => {
            const data = this._linkService.getTrackingData(x.anchor);

            const cfg = {
                ...x.link.tracking?.parameters,
                link_classes: data.linkClasses,
                link_id: data.linkId,
                link_text: data.linkText,
                link_url: data.linkUrl,
                outbound: data.outbound,
            };

            this._gtmService.track(TrackingEvents.gtm['footerClick']([cfg]));

            const cfg2 = {
                category: 'footer',
                identifier: x.link.tracking?.parameters['identifier'],
                element_classes: data.linkClasses.split(' '),
                target_url: data.linkUrl ? data.linkUrl : this.Window?.location.href,
                element_content: data.linkText,
                outbound: data.outbound !== 'FALSE',

                context: [],
            };

            this._snowplowService.track('linkClick', cfg2);
        });
    }
    //#endregion
}
